import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { courseService } from '../../../services/courseService';

const SearchCoursesModal = ({ onClose, user }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedCourseType, setSelectedCourseType] = useState('all');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'engineering', label: 'Engineering & Technology' },
    { value: 'medical', label: 'Medical & Health Sciences' },
    { value: 'management', label: 'Business & Management' },
    { value: 'commerce', label: 'Commerce & Finance' },
    { value: 'science', label: 'Sciences' },
    { value: 'arts', label: 'Arts & Humanities' },
    { value: 'law', label: 'Law' },
    { value: 'professional', label: 'Professional Courses' },
    { value: 'diploma', label: 'Diploma Programs' },
    { value: 'education', label: 'Education' },
    { value: 'research', label: 'Research' }
  ];

  const levels = [
    { value: 'all', label: 'All Levels' },
    { value: 'undergraduate', label: 'Undergraduate' },
    { value: 'postgraduate', label: 'Postgraduate' },
    { value: 'postgraduate_diploma', label: 'Post Graduate Diploma' },
    { value: 'phd', label: 'Ph.D' },
    { value: 'integrated', label: 'Integrated' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'professional', label: 'Professional' }
  ];

  const courseTypes = [
    { value: 'all', label: 'All Course Types' },
    { value: 'B.Tech', label: 'B.Tech' },
    { value: 'M.Tech', label: 'M.Tech' },
    { value: 'MBBS', label: 'MBBS' },
    { value: 'BDS', label: 'BDS' },
    { value: 'MBA', label: 'MBA' },
    { value: 'BBA', label: 'BBA' },
    { value: 'B.Com', label: 'B.Com' },
    { value: 'M.Com', label: 'M.Com' },
    { value: 'B.Sc', label: 'B.Sc' },
    { value: 'M.Sc', label: 'M.Sc' },
    { value: 'BA', label: 'BA' },
    { value: 'MA', label: 'MA' },
    { value: 'LLB', label: 'LLB' },
    { value: 'CA', label: 'CA' },
    { value: 'CS', label: 'CS' },
    { value: 'B.Arch', label: 'B.Arch' },
    { value: 'BCA', label: 'BCA' },
    { value: 'MCA', label: 'MCA' }
  ];

  useEffect(() => {
    fetchCourses();
  }, [selectedCategory, selectedLevel, selectedCourseType]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courseService?.getAllCourses({
        category: selectedCategory,
        level: selectedLevel,
        courseType: selectedCourseType,
        limit: 50
      });
      setCourses(data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courseService?.getAllCourses({
        searchQuery,
        category: selectedCategory,
        level: selectedLevel,
        courseType: selectedCourseType,
        limit: 50
      });
      setCourses(data || []);
    } catch (err) {
      console.error('Error searching courses:', err);
      setError('Failed to search courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses?.filter(course => {
    if (!searchQuery) return true;
    const query = searchQuery?.toLowerCase();
    return course?.courseName?.toLowerCase()?.includes(query) ||
           course?.specialization?.toLowerCase()?.includes(query) ||
           course?.institute?.name?.toLowerCase()?.includes(query);
  });

  const formatFees = (fees, showFee) => {
    if (showFee === false) return 'Contact Institute';
    if (!fees) return 'Contact Institute';
    const amount = parseFloat(fees);
    if (amount >= 100000) {
      return `₹${(amount / 100000)?.toFixed(2)} Lakh`;
    }
    return `₹${amount?.toLocaleString('en-IN')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Icon name="Search" size={28} color="white" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold">Search Courses</h2>
                <p className="text-white/90 text-sm">Find your perfect program from top universities</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={24} color="white" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="md:col-span-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Search by course name, specialization, or institute..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                  iconName="Search"
                />
                <Button onClick={handleSearch} iconName="Search">
                  Search
                </Button>
              </div>
            </div>
            <Select
              value={selectedCategory}
              onChange={(val) => setSelectedCategory(val)}
              options={categories}
            />
            <Select
              value={selectedLevel}
              onChange={(val) => setSelectedLevel(val)}
              options={levels}
            />
            <Select
              value={selectedCourseType}
              onChange={(val) => setSelectedCourseType(val)}
              options={courseTypes}
            />
            <Button variant="outline" iconName="RotateCcw" iconPosition="left" fullWidth onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
              setSelectedLevel('all');
              setSelectedCourseType('all');
            }}>
              Reset
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading courses...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" size={32} color="#ef4444" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Courses</h3>
              <p className="text-sm text-muted-foreground mb-4">{error}</p>
              <Button onClick={fetchCourses} iconName="RefreshCw">Retry</Button>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Found <span className="font-semibold text-foreground">{filteredCourses?.length}</span> courses
                </p>
                {filteredCourses?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Showing results from {courses?.length} total courses
                  </p>
                )}
              </div>

              <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                {filteredCourses?.map((course) => (
                  <div
                    key={course?.id}
                    className="p-4 border-2 border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-2">
                          {course?.institute?.logoUrl && (
                            <img
                              src={course?.institute?.logoUrl}
                              alt={course?.institute?.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                              {course?.courseName}
                            </h3>
                            {course?.specialization && (
                              <p className="text-sm text-primary font-medium mb-1">
                                {course?.specialization}
                              </p>
                            )}
                            {course?.institute && (
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Icon name="Building2" size={14} color="var(--color-muted-foreground)" />
                                {course?.institute?.name}
                                {course?.institute?.city && (
                                  <span className="ml-1">• {course?.institute?.city}</span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {course?.courseType && (
                            <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-md">
                              {course?.courseType}
                            </span>
                          )}
                          {course?.level && (
                            <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded-md capitalize">
                              {course?.level}
                            </span>
                          )}
                          {course?.accreditation && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-md">
                              {course?.accreditation}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                            <span>{course?.duration}</span>
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Icon name="IndianRupee" size={14} color="var(--color-muted-foreground)" />
                            <span>{formatFees(course?.fees, course?.show_fee)}</span>
                          </div>
                          {course?.placementStats && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Icon name="TrendingUp" size={14} color="var(--color-muted-foreground)" />
                              <span className="text-xs">{course?.placementStats}</span>
                            </div>
                          )}
                        </div>
                        {course?.eligibility && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="font-medium">Eligibility:</span> {course?.eligibility}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="right"
                        onClick={() => {
                          if (!user) {
                            onClose();
                            navigate('/student-registration');
                            return;
                          }
                          if (course?.institute?.id) {
                            onClose();
                            navigate(`/virtual-booth/${course?.institute?.id}`);
                          }
                        }}
                      >
                        {user ? 'View Details' : 'Register to View'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredCourses?.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No courses found</h3>
                  <p className="text-sm text-muted-foreground mb-4">Try adjusting your search filters or search query</p>
                  <Button variant="outline" onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                    setSelectedLevel('all');
                    setSelectedCourseType('all');
                  }}>
                    Clear All Filters
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchCoursesModal;